import type {DeepMemoExoticComponent, DeepNamedExoticComponent, FunctionComponent} from 'react'
import isEqual from 'react-fast-compare'
import {omit, pick} from 'remeda'

import shallowEqual from './shallowEqual'
/* eslint-disable @typescript-eslint/no-explicit-any -- intentional */

// NOTE: we have to use currying because typescript doesn't support Partial Type Argument Inference yet https://github.com/microsoft/TypeScript/issues/26242
// So if we use deepMemo<P> and leave out the propsToDeepEqual, typescript will not infer it, but throw an error to your face.
/**
 * This function behave like `React.memo`, but with deep equal for props instead of shallow equal.
 * Originally this utility is used to avoid re-rendering of components that accepts `children` props, because when JSX passed, it always create new reference each time.
 *
 * @param Component - The component to selectively deep memoize.
 * @returns A function that returns the memoized component, have a single parameter: propsToDeepEqual - The props to deep equal (in addition to `children`).
 * - `string[]` - Deep equal the props in the array, these usually be the props that are ReactNode (react element), or not primitive: object, array, Map, Set. Note that we cannot deepEqual functions.
 * - `'all'` - Deep equal all props..
 */
export default function deepMemo<P extends object>(
  Component: FunctionComponent<MemoizedProps<P>>,
): <const TKeys extends (keyof P)[] | 'all' | undefined = undefined>(
  propsToDeepEqual?: TKeys,
) => DeepNamedExoticComponent<
  P,
  TKeys extends unknown[] ? TKeys[number] : TKeys extends string ? keyof P : never
>
export default function deepMemo<T extends ComponentType<any>>(
  Component: T,
): <const TKeys extends (keyof ComponentProps<T>)[] | 'all' | undefined = undefined>(
  propsToDeepEqual?: TKeys,
) => DeepMemoExoticComponent<
  T,
  TKeys extends unknown[] ? TKeys[number] : TKeys extends string ? keyof ComponentProps<T> : never
>
export default function deepMemo<P extends object>(
  Component: FunctionComponent<P> | ComponentType<P>,
) {
  return <const TKeys extends (keyof P)[] | 'all' | undefined = undefined>(
    propsToDeepEqual?: TKeys,
  ) => {
    return memo(Component, (prevProps, nextProps) => {
      if (propsToDeepEqual === 'all') {
        return isEqual(prevProps, nextProps)
      }

      propsToDeepEqual ??= [] as unknown as Exclude<TKeys, 'all' | undefined>

      propsToDeepEqual.push('children' as keyof P)

      return (
        shallowEqual(omit(prevProps, propsToDeepEqual), omit(nextProps, propsToDeepEqual)) &&
        isEqual(pick(prevProps, propsToDeepEqual), pick(nextProps, propsToDeepEqual))
      )
    }) as DeepMemoExoticComponent<
      ComponentType<P>,
      TKeys extends unknown[] ? TKeys[number] : TKeys extends string ? keyof P : never
    >
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any -- intentional */
